"use client"

import { useState } from "react"
import "./EvolutionLab.css"

const EvolutionLab = ({ gameState, setGameState, addNotification }) => {
  const [selectedTraits, setSelectedTraits] = useState({
    intelligence: 50,
    strength: 50,
    adaptability: 50,
    longevity: 50,
  })

  const [isEvolving, setIsEvolving] = useState(false)

  const traitDescriptions = {
    intelligence: "Increases research speed and unlocks advanced technologies",
    strength: "Improves construction speed and resource gathering efficiency",
    adaptability: "Reduces environmental damage and improves survival rates",
    longevity: "Increases population growth and reduces death rates",
  }

  const evolutionCost = 100

  const handleTraitChange = (trait, value) => {
    setSelectedTraits((prev) => ({
      ...prev,
      [trait]: value,
    }))
  }

  const getTotalPoints = () => {
    return Object.values(selectedTraits).reduce((sum, value) => sum + value, 0)
  }

  const canEvolve = () => {
    return gameState.user.evolutionPoints >= evolutionCost && getTotalPoints() <= 200
  }

  const startEvolution = () => {
    if (!canEvolve()) {
      addNotification({
        type: "error",
        title: "Evolution Failed",
        description: "Insufficient evolution points or trait points exceed limit",
      })
      return
    }

    setIsEvolving(true)

    setTimeout(() => {
      setGameState((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          evolutionPoints: prev.user.evolutionPoints - evolutionCost,
          intelligence: Math.min(100, prev.user.intelligence + Math.floor(selectedTraits.intelligence / 10)),
          engineering: Math.min(100, prev.user.engineering + Math.floor(selectedTraits.strength / 10)),
          biology: Math.min(100, prev.user.biology + Math.floor(selectedTraits.adaptability / 10)),
        },
        colonyHealth: Math.min(100, (prev.colonyHealth || 80) + Math.floor(selectedTraits.longevity / 10)),
      }))

      addNotification({
        type: "success",
        title: "Evolution Complete",
        description: "Your colonists have successfully adapted!",
      })
      setIsEvolving(false)
    }, 3000)
  }

  return (
    <div className="evolution-lab">
      <div className="evolution-header">
        <h2 className="section-title">
          <span className="title-icon">🧬</span>
          Evolution Laboratory
        </h2>
        <p className="section-subtitle">Guide the evolution of your colonists</p>
      </div>

      <div className="evolution-content">
        <div className="evolution-points">
          <div className="points-display">
            <span className="points-icon">⚡</span>
            <span className="points-value">{gameState.user.evolutionPoints}</span>
            <span className="points-label">Evolution Points</span>
          </div>
          <div className="points-cost">
            <span>Cost: {evolutionCost} points</span>
          </div>
        </div>

        <div className="trait-selection">
          <h3 className="panel-title">Trait Allocation</h3>
          <p className="trait-info">Total Points: {getTotalPoints()}/200</p>

          <div className="traits-grid">
            {Object.entries(selectedTraits).map(([trait, value]) => (
              <div key={trait} className="trait-card">
                <div className="trait-header">
                  <h4 className="trait-name">
                    {trait === "intelligence" && "🧠"}
                    {trait === "strength" && "💪"}
                    {trait === "adaptability" && "🌿"}
                    {trait === "longevity" && "❤️"}
                    {trait.charAt(0).toUpperCase() + trait.slice(1)}
                  </h4>
                  <span className="trait-value">{value}</span>
                </div>

                <div className="trait-slider">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={value}
                    onChange={(e) => handleTraitChange(trait, Number.parseInt(e.target.value))}
                    disabled={isEvolving}
                    className="slider"
                  />
                </div>

                <p className="trait-description">{traitDescriptions[trait]}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="evolution-preview">
          <h3 className="panel-title">Evolution Preview</h3>
          <div className="preview-grid">
            <div className="preview-item">
              <span className="preview-label">Intelligence Gain:</span>
              <span className="preview-value">+{Math.floor(selectedTraits.intelligence / 10)}</span>
            </div>
            <div className="preview-item">
              <span className="preview-label">Engineering Gain:</span>
              <span className="preview-value">+{Math.floor(selectedTraits.strength / 10)}</span>
            </div>
            <div className="preview-item">
              <span className="preview-label">Biology Gain:</span>
              <span className="preview-value">+{Math.floor(selectedTraits.adaptability / 10)}</span>
            </div>
            <div className="preview-item">
              <span className="preview-label">Colony Health Gain:</span>
              <span className="preview-value">+{Math.floor(selectedTraits.longevity / 10)}%</span>
            </div>
          </div>
        </div>

        <div className="evolution-actions">
          {isEvolving ? (
            <div className="evolution-progress">
              <div className="progress-spinner"></div>
              <span>Evolution in progress...</span>
            </div>
          ) : (
            <button className="evolve-btn" onClick={startEvolution} disabled={!canEvolve()}>
              {canEvolve() ? "🧬 Begin Evolution" : "Cannot Evolve"}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default EvolutionLab
